'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title?: string;
  action?: ToastAction;
  /** ms; 0 = until dismissed. Default 5000, or 0 when an action is set. */
  duration?: number;
}

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  title?: string;
  action?: ToastAction;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, options?: number | ToastOptions) => string;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', options?: number | ToastOptions) => {
      const opts: ToastOptions =
        typeof options === 'number' ? { duration: options } : (options ?? {});
      const duration = opts.duration ?? (opts.action ? 0 : 5000);
      const id = Math.random().toString(36).slice(2, 11);

      setToasts((prev) => [...prev, { id, message, type, title: opts.title, action: opts.action }]);

      if (duration > 0) {
        setTimeout(() => {
          hideToast(id);
        }, duration);
      }
      return id;
    },
    [hideToast],
  );

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <ToastContainer toasts={toasts} onHide={hideToast} />
    </ToastContext.Provider>
  );
}

interface ToastContainerProps {
  toasts: ToastItem[];
  onHide: (id: string) => void;
}

// Always mounted: a live region must exist before content is inserted for screen readers
// to announce it. z-[60] keeps toasts above Dialog's z-50 scrim.
function ToastContainer({ toasts, onHide }: ToastContainerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed top-4 right-4 z-[60] space-y-2"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onHide={onHide} />
      ))}
    </div>
  );
}

const ACCENT: Record<ToastType, string> = {
  success: 'border-l-success',
  error: 'border-l-danger',
  info: 'border-l-info',
  warning: 'border-l-warning',
};

interface ToastProps {
  toast: ToastItem;
  onHide: (id: string) => void;
}

function Toast({ toast, onHide }: ToastProps) {
  const t = useTranslations('Common');
  const { action } = toast;

  return (
    <div
      data-toast=""
      className={cn(
        'bg-card text-foreground border-border pointer-events-auto flex w-[min(400px,calc(100vw-2rem))] items-start gap-3 rounded-lg border border-l-4 p-4 shadow-lg transition-all duration-300 ease-in-out',
        ACCENT[toast.type],
      )}
    >
      <div className="min-w-0 flex-1 text-sm">
        {toast.title ? <p className="font-semibold">{toast.title}</p> : null}
        <p className={toast.title ? 'text-muted' : 'font-medium'}>{toast.message}</p>
      </div>
      {action ? (
        <button
          type="button"
          onClick={() => {
            action.onClick();
            onHide(toast.id);
          }}
          className="text-accent shrink-0 text-sm font-semibold underline-offset-2 hover:underline"
        >
          {action.label}
        </button>
      ) : null}
      <button
        type="button"
        aria-label={t('dismissNotification')}
        onClick={() => onHide(toast.id)}
        className="text-muted hover:text-foreground shrink-0"
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
